import * as React from "react";
import {
  List, Datagrid, TextField, BooleanField,
  useRecordContext, useListContext,
  TopToolbar, ExportButton, DeleteButton,
} from "react-admin";
import { Alert, Box, Button, Card, CardContent, Stack, TextField as MuiTextField } from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CampaignIcon from "@mui/icons-material/Campaign";
import { EnumChip } from "../../components/EnumChip";
import { JsonField } from "../../components/JsonField";
import { ConfirmActionButton } from "../../components/ConfirmActionButton";
import { NOTIFICATION_TYPE } from "../../enums";
import { httpClient } from "../../api/httpClient";
import { API_BASE_URL } from "../../api/config";
import { FrDateField } from "../../components/FrDateField";
import { UserReferenceInput } from "../../components/RelationAutocomplete";

// Le backend n'expose que les notifications d'UN utilisateur :
// GET /notifications/user/{userId} (resourceMap.dynamicPath). Pas de
// lecture / édition unitaire : actions « lue » et suppression par ligne.
const filters = [
  <UserReferenceInput key="userId" source="userId" label="Utilisateur" alwaysOn />,
];

const MarkRead = () => {
  const r = useRecordContext();
  if (!r || r.read) return null;
  return (
    <ConfirmActionButton label="Marquer lue" color="primary" variant="text"
      confirmTitle="Marquer comme lue" confirmText="Confirmer ?"
      successMessage="Marquée comme lue"
      onConfirm={() => httpClient(`${API_BASE_URL}/notifications/${r.id}/read`, { method: "PUT" })}
    />
  );
};

const BulkUserActions = () => {
  const [userId, setUserId] = React.useState("");
  const markAll = async () => {
    if (!userId.trim()) return;
    try {
      await httpClient(`${API_BASE_URL}/notifications/user/${userId}/read-all`, { method: "PUT" });
      alert("Toutes les notifications marquées comme lues");
    } catch (e) {
      alert(`Erreur: ${e.message}`);
    }
  };
  const deleteAll = async () => {
    if (!userId.trim()) return;
    if (!window.confirm("Supprimer toutes les notifications de cet utilisateur ?")) return;
    try {
      await httpClient(`${API_BASE_URL}/notifications/user/${userId}`, { method: "DELETE" });
      alert("Notifications supprimées");
    } catch (e) {
      alert(`Erreur: ${e.message}`);
    }
  };
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <NotificationsActiveIcon color="primary" />
          <MuiTextField size="small" label="ID utilisateur" value={userId}
            onChange={(e) => setUserId(e.target.value)} />
          <Button startIcon={<DoneAllIcon />} variant="outlined" color="primary"
            onClick={markAll} disabled={!userId.trim()}>
            Marquer tout lu
          </Button>
          <Button startIcon={<DeleteSweepIcon />} variant="outlined" color="error"
            onClick={deleteAll} disabled={!userId.trim()}>
            Supprimer toutes
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

// POST /notifications/send-notification prend un NotificationDTO
// { token, titre, body, data?, badgeCount? } : envoi vers UN jeton d'appareil
// FCM (pas de ciblage par userId ni de broadcast côté backend, et les jetons
// des utilisateurs ne sont pas exposés par l'API). Le backend répond 200 avec
// une chaîne texte, y compris en cas d'échec ("Failed to send notification…").
const PushBroadcaster = () => {
  const [title, setTitle] = React.useState("");
  const [body, setBody] = React.useState("");
  const [token, setToken] = React.useState("");
  const send = async () => {
    if (!token.trim() || !title || !body) return;
    try {
      const res = await httpClient(`${API_BASE_URL}/notifications/send-notification`, {
        method: "POST",
        body: JSON.stringify({ token: token.trim(), titre: title, body }),
      });
      const text = String(res.body || "");
      if (/^failed/i.test(text.trim())) {
        alert(`Échec de l'envoi : ${text}`);
        return;
      }
      alert("Push envoyée");
      setTitle(""); setBody("");
    } catch (e) {
      alert(`Erreur: ${e.message}`);
    }
  };
  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CampaignIcon color="primary" />
            <strong>Envoyer une push notification (Firebase)</strong>
          </Stack>
          <Alert severity="info">
            Envoi vers un jeton d'appareil FCM précis (notification de test).
            Le backend ne permet pas de cibler un utilisateur par son ID.
          </Alert>
          <MuiTextField size="small" label="Jeton FCM de l'appareil" required
            value={token} onChange={(e) => setToken(e.target.value)} />
          <MuiTextField size="small" label="Titre" value={title}
            onChange={(e) => setTitle(e.target.value)} />
          <MuiTextField size="small" label="Corps" multiline minRows={3}
            value={body} onChange={(e) => setBody(e.target.value)} />
          <Box>
            <Button variant="contained" onClick={send} disabled={!token.trim() || !title || !body}>
              Envoyer
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);

const UserHint = () => {
  const { filterValues } = useListContext();
  if (filterValues?.userId) return null;
  return (
    <Alert severity="info" sx={{ m: 2 }}>
      Renseignez l'ID d'un utilisateur pour afficher ses notifications (ou ouvrez sa fiche).
    </Alert>
  );
};

const NotifList = () => (
  <Box>
    <List filters={filters} actions={<ListActions />} empty={false}
      sort={{ field: "createdAt", order: "DESC" }} resource="notifications">
      <UserHint />
      <Datagrid rowClick={false} bulkActionButtons={false} expand={<JsonField source="__all__" />}>
        <TextField source="title" label="Titre" />
        <TextField source="body" label="Message" />
        <EnumChip source="type" enumObj={NOTIFICATION_TYPE} />
        <BooleanField source="read" label="Lue" />
        <FrDateField source="createdAt" label="Date" showTime />
        <MarkRead />
        <DeleteButton mutationMode="pessimistic" />
      </Datagrid>
    </List>
    <Box sx={{ p: 2 }}>
      <BulkUserActions />
      <PushBroadcaster />
    </Box>
  </Box>
);

export default {
  list: NotifList,
};
